/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from "react";

import "./Routes.css";
import { Section, Button } from "react-bulma-components";

import { getPreparedRouteByAPIResponse } from "api/prepared_routes";
import {
  Navbar,
  RoutesTable,
  NewWorkplaceDialog,
  NewRouteDialog,
  RouteApplicationDialog,
  SystemNotification,
} from "components";
import { ExpirationDateDialog } from "components/ExpirationDateDialog/ExpirationDateDialog";
import { GlobalErrorScreen } from "components/GlobalErrorScreen";
import { MODE as ROUTE_APPLICATION_DIALOG_MODE } from "components/RouteApplicationDialog/RouteApplicationDialog";
import { suggestedPreparedRouteDetailFare } from "components/TransitsTableForm/FareRow";
import { TRANSPORTATION_TYPE } from "enums/transportationType";
import { getFormattedComment } from "helpers/comment";
import {
  useGetUserAddressApi,
  useGetPreparedRoutesApi,
  usePostPreparedRoutesApi,
  useDelPreparedRouteApi,
  useUpdatePreparedRoutePriorityApi,
  useUpdatePreparedRoutesPreparedRouteIdPreparedRouteApprovalsApi,
  useUpdatePreparedRouteExpirationDateApi,
} from "hooks/api";
import Address from "models/address";
import { PreparedRoute, PreparedRouteApproval } from "models/preparedRoute";
import { SuggestedPreparedRoute } from "models/suggestedPreparedRoute";
import { AuthContext } from "providers/CognitoAuthProvider";

export interface PreparedRoutesByWorkplace {
  arrivalId: number;
  arrivalName?: string;
  arrivalAddressInvalidatedAt?: Date;
  departureId: number;
  departureName?: string;
  preparedRoutes: PreparedRoute[];
}

interface RoutesState {
  preparedRoutes: PreparedRoute[];
  preparedRoutesByWorkplaces: PreparedRoutesByWorkplace[];
  isPreparedRoutesLoaded: boolean;

  isNewRouteDialogVisible: boolean;
  isCreatePreparedRouteInProgress: boolean;
  arrivalId?: number;
  arrivalName?: string;
  departureId?: number;
  departureName?: string;

  isRouteApplicationDialogVisible: boolean;
  isUpdatePreparedRouteCompleted: boolean;
  isUpdatePreparedRouteInProgress: boolean;
  selectedPreparedRoute?: PreparedRoute;
  comment?: string;
  routeApplicationDialogMode?: string;

  isExpirationDateDialogVisible: boolean;
  isExpirationDateInProgress: boolean;

  isNewWorkplaceDialogVisible: boolean;
  selectedAddress?: Address;
  homeAddress?: Address;
  isHomeAddressNotLoaded: boolean;
}

const initialState: RoutesState = {
  preparedRoutes: [],
  preparedRoutesByWorkplaces: [],
  isPreparedRoutesLoaded: false,

  isNewRouteDialogVisible: false,
  isCreatePreparedRouteInProgress: false,

  isRouteApplicationDialogVisible: false,
  isUpdatePreparedRouteCompleted: false,
  isUpdatePreparedRouteInProgress: false,

  isExpirationDateDialogVisible: false,
  isExpirationDateInProgress: false,

  isNewWorkplaceDialogVisible: false,
  isHomeAddressNotLoaded: false,
};

interface ChangeCommentAction {
  type: "CHANGE_COMMENT";
  payload: {
    comment: string;
  };
}

interface SetIsCreatePreparedRouteInProgressAction {
  type: "SET_IS_CREATE_PREPARED_ROUTE_IN_PROGRESS";
  payload: {
    isCreatePreparedRouteInProgress: boolean;
  };
}

interface SetIsPreparedRoutesLoadedAction {
  type: "SET_IS_PREPARED_ROUTES_LOADED";
  payload: {
    isPreparedRoutesLoaded: boolean;
  };
}

interface SetIsUpdatePreparedRouteCompletedAction {
  type: "SET_IS_UPDATE_PREPARED_ROUTE_COMPLETED";
  payload: {
    isUpdatePreparedRouteCompleted: boolean;
    selectedPreparedRoute: PreparedRoute;
  };
}

interface SetIsUpdatePreparedRouteInProgressAction {
  type: "SET_IS_UPDATE_PREPARED_ROUTE_IN_PROGRESS";
  payload: {
    isUpdatePreparedRouteInProgress: boolean;
  };
}

interface SetPreparedRouteAction {
  type: "SET_PREPARED_ROUTE";
  payload: {
    preparedRoute: PreparedRoute;
  };
}

interface SetPreparedRoutesAction {
  type: "SET_PREPARED_ROUTES";
  payload: {
    preparedRoutes: PreparedRoute[];
  };
}

interface ToggleNewRouteDialogAction {
  type: "TOGGLE_NEW_ROUTE_DIALOG";
  payload: {
    departureId?: number;
    departureName?: string;
    arrivalId?: number;
    arrivalName?: string;
  };
}

interface ToggleNewRouteDialogToChangeAction {
  type: "TOGGLE_NEW_ROUTE_DIALOG_TO_CHANGE";
  payload: {
    departureId: number;
    departureName: string;
    arrivalId: number;
    arrivalName: string;
    preparedRouteToChange: PreparedRoute;
  };
}

interface RegisterWorkplaceAction {
  type: "REGISTER_WORKPLACE";
  payload: {
    selectedAddress: Address;
    home?: Address;
  };
}

interface SetHomeAddressAction {
  type: "SET_HOME_ADDRESS";
  payload: {
    homeAddress: Address;
  };
}

interface ToggleNewWorkplaceDialogAction {
  type: "TOGGLE_NEW_WORKPLACE_DIALOG";
}

interface ToggleRouteApplicationDialogAction {
  type: "TOGGLE_ROUTE_APPLICATION_DIALOG";
}

interface ToggleRouteApplicationDialogToApplyAction {
  type: "TOGGLE_ROUTE_APPLICATION_DIALOG_TO_APPLY";
  payload: {
    selectedPreparedRoute: PreparedRoute;
  };
}

interface ToggleRouteApplicationDialogToCancelAction {
  type: "TOGGLE_ROUTE_APPLICATION_DIALOG_TO_CANCEL";
  payload: {
    selectedPreparedRoute: PreparedRoute;
  };
}

interface ToggleExpirationDateDialogAction {
  type: "TOGGLE_EXPIRATION_DATE_DIALOG";
  payload: {
    preparedRoute?: PreparedRoute;
  };
}

interface SetIsExpirationDateInProgressAction {
  type: "SET_IS_EXPIRATION_DATE_IN_PROGRESS";
  payload: {
    isExpirationDateInProgress: boolean;
  };
}

type RoutesActions =
  | ChangeCommentAction
  | SetIsCreatePreparedRouteInProgressAction
  | SetIsPreparedRoutesLoadedAction
  | SetIsUpdatePreparedRouteCompletedAction
  | SetIsUpdatePreparedRouteInProgressAction
  | SetPreparedRouteAction
  | SetPreparedRoutesAction
  | ToggleNewRouteDialogAction
  | ToggleNewRouteDialogToChangeAction
  | RegisterWorkplaceAction
  | SetHomeAddressAction
  | ToggleNewWorkplaceDialogAction
  | ToggleRouteApplicationDialogAction
  | ToggleRouteApplicationDialogToApplyAction
  | ToggleRouteApplicationDialogToCancelAction
  | ToggleExpirationDateDialogAction
  | SetIsExpirationDateInProgressAction;

const getPreparedRoutesByWorkplaces = (preparedRoutes: PreparedRoute[]) => {
  const preparedRoutesByWorkplaces: PreparedRoutesByWorkplace[] = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const preparedRoute of preparedRoutes) {
    let found = false;
    // eslint-disable-next-line no-restricted-syntax
    for (const preparedRoutesByWorkplace of preparedRoutesByWorkplaces) {
      if (preparedRoutesByWorkplace.arrivalId === preparedRoute.arrivalId) {
        preparedRoutesByWorkplace.preparedRoutes.push(preparedRoute);
        found = true;
        break;
      }
    }
    if (!found) {
      preparedRoutesByWorkplaces.push({
        arrivalId: preparedRoute.arrivalId,
        arrivalName: preparedRoute.arrivalName,
        arrivalAddressInvalidatedAt: preparedRoute.arrivalAddressInvalidatedAt,
        departureId: preparedRoute.departureId,
        departureName: preparedRoute.departureName,
        preparedRoutes: [preparedRoute],
      });
    }
  }

  return preparedRoutesByWorkplaces;
};

const setPreparedRoutes = (
  state: RoutesState,
  action: SetPreparedRoutesAction
) => {
  const { preparedRoutes } = action.payload;

  state.preparedRoutes = preparedRoutes;
  state.preparedRoutesByWorkplaces =
    getPreparedRoutesByWorkplaces(preparedRoutes);
};

const setPreparedRoute = (
  state: RoutesState,
  action: SetPreparedRouteAction
) => {
  const { preparedRoutes } = state;
  const { preparedRoute } = action.payload;
  for (let i = 0; i < preparedRoutes.length; i += 1) {
    if (preparedRoutes[i].id === preparedRoute.id) {
      preparedRoutes[i] = preparedRoute;
    }
  }

  state.preparedRoutesByWorkplaces =
    getPreparedRoutesByWorkplaces(preparedRoutes);
};

const reducer: React.Reducer<RoutesState, RoutesActions> = (
  currentState,
  action
) => {
  const newState: RoutesState = { ...currentState };

  switch (action.type) {
    case "CHANGE_COMMENT":
      newState.comment = action.payload.comment;
      break;
    case "SET_IS_CREATE_PREPARED_ROUTE_IN_PROGRESS":
      newState.isCreatePreparedRouteInProgress =
        action.payload.isCreatePreparedRouteInProgress;
      break;
    case "SET_IS_PREPARED_ROUTES_LOADED":
      newState.isPreparedRoutesLoaded = action.payload.isPreparedRoutesLoaded;
      break;
    case "SET_IS_UPDATE_PREPARED_ROUTE_COMPLETED":
      newState.selectedPreparedRoute = action.payload.selectedPreparedRoute;
      newState.isUpdatePreparedRouteCompleted =
        action.payload.isUpdatePreparedRouteCompleted;
      break;
    case "SET_IS_UPDATE_PREPARED_ROUTE_IN_PROGRESS":
      newState.isUpdatePreparedRouteInProgress =
        action.payload.isUpdatePreparedRouteInProgress;
      break;
    case "SET_PREPARED_ROUTE":
      setPreparedRoute(newState, action);
      break;
    case "SET_PREPARED_ROUTES":
      setPreparedRoutes(newState, action);
      break;
    case "TOGGLE_NEW_ROUTE_DIALOG":
      newState.isNewRouteDialogVisible = !newState.isNewRouteDialogVisible;
      newState.arrivalId = action.payload.arrivalId;
      newState.arrivalName = action.payload.arrivalName;
      newState.departureId = action.payload.departureId;
      newState.departureName = action.payload.departureName;
      if (!newState.isNewRouteDialogVisible) {
        newState.selectedPreparedRoute = undefined;
      }
      break;
    case "TOGGLE_NEW_ROUTE_DIALOG_TO_CHANGE":
      newState.isNewRouteDialogVisible = true;
      newState.arrivalId = action.payload.arrivalId;
      newState.arrivalName = action.payload.arrivalName;
      newState.departureId = action.payload.departureId;
      newState.departureName = action.payload.departureName;
      newState.selectedPreparedRoute = action.payload.preparedRouteToChange;
      break;
    case "REGISTER_WORKPLACE":
      if (action.payload.home) {
        newState.preparedRoutesByWorkplaces.unshift({
          departureId: action.payload.home.id,
          departureName: action.payload.home.name,
          arrivalId: action.payload.selectedAddress.id,
          arrivalName: action.payload.selectedAddress.name,
          preparedRoutes: [],
        });
      }
      newState.isNewWorkplaceDialogVisible =
        !newState.isNewWorkplaceDialogVisible;
      break;
    case "SET_HOME_ADDRESS":
      newState.homeAddress = action.payload.homeAddress;
      newState.isHomeAddressNotLoaded = action.payload.homeAddress === null;
      break;
    case "TOGGLE_NEW_WORKPLACE_DIALOG":
      newState.isNewWorkplaceDialogVisible =
        !newState.isNewWorkplaceDialogVisible;
      break;
    case "TOGGLE_ROUTE_APPLICATION_DIALOG":
      newState.isRouteApplicationDialogVisible = false;
      newState.isUpdatePreparedRouteCompleted = false;
      newState.isUpdatePreparedRouteInProgress = false;
      newState.selectedPreparedRoute = undefined;
      newState.comment = undefined;
      newState.routeApplicationDialogMode = undefined;
      break;
    case "TOGGLE_ROUTE_APPLICATION_DIALOG_TO_APPLY":
      newState.isRouteApplicationDialogVisible = true;
      newState.isUpdatePreparedRouteCompleted = false;
      newState.isUpdatePreparedRouteInProgress = false;
      newState.selectedPreparedRoute = action.payload.selectedPreparedRoute;
      newState.comment = "";
      newState.routeApplicationDialogMode = ROUTE_APPLICATION_DIALOG_MODE.apply;
      break;
    case "TOGGLE_ROUTE_APPLICATION_DIALOG_TO_CANCEL":
      newState.isRouteApplicationDialogVisible = true;
      newState.isUpdatePreparedRouteCompleted = false;
      newState.isUpdatePreparedRouteInProgress = false;
      newState.selectedPreparedRoute = action.payload.selectedPreparedRoute;
      newState.comment = "";
      newState.routeApplicationDialogMode =
        ROUTE_APPLICATION_DIALOG_MODE.cancel;
      break;
    case "TOGGLE_EXPIRATION_DATE_DIALOG":
      newState.selectedPreparedRoute = action.payload.preparedRoute;
      newState.isExpirationDateDialogVisible =
        !newState.isExpirationDateDialogVisible;
      break;
    case "SET_IS_EXPIRATION_DATE_IN_PROGRESS":
      newState.isExpirationDateInProgress =
        action.payload.isExpirationDateInProgress;
      break;
    default:
      throw new Error();
  }
  return newState;
};

const createPreparedRoute = async (
  suggestedPreparedRoute: SuggestedPreparedRoute,
  postApi: ReturnType<typeof usePostPreparedRoutesApi>
) => {
  const preparedRouteDetails =
    suggestedPreparedRoute.suggestedPreparedRouteDetails;

  let type: "public_transportation" | "my_car";
  const { transportationType } = preparedRouteDetails[0];
  if (
    transportationType === TRANSPORTATION_TYPE.walk ||
    transportationType === TRANSPORTATION_TYPE.train ||
    transportationType === TRANSPORTATION_TYPE.bus ||
    transportationType === TRANSPORTATION_TYPE.other
  ) {
    type = "public_transportation";
  } else {
    type = "my_car";
  }

  return postApi(
    {
      departureId: suggestedPreparedRoute.departureId,
      arrivalId: suggestedPreparedRoute.arrivalId,
      type,
      details: preparedRouteDetails,
    },
    suggestedPreparedRouteDetailFare
  );
};

const updatePreparedRoutePriority = async (
  preparedRoute: PreparedRoute,
  updateApi: ReturnType<typeof useUpdatePreparedRoutePriorityApi>
) => {
  if (!preparedRoute) throw new Error("preparedRoute is not selected.");
  return updateApi(preparedRoute.id);
};

const updatePreparedRouteExpirationDate = async (
  preparedRoute: PreparedRoute,
  updateApi: ReturnType<typeof useUpdatePreparedRouteExpirationDateApi>
) => {
  if (!preparedRoute) throw new Error("preparedRoute is not selected.");
  return updateApi(
    preparedRoute.id,
    preparedRoute.startDate,
    preparedRoute.endDate
  );
};

const updatePreparedRouteToApply = async (
  state: RoutesState,
  updateApi: ReturnType<
    typeof useUpdatePreparedRoutesPreparedRouteIdPreparedRouteApprovalsApi
  >
) => {
  const { selectedPreparedRoute: preparedRoute, comment } = state;

  if (!preparedRoute) throw new Error("preparedRoute is not selected.");

  return updateApi(
    preparedRoute.id,
    "applying",
    getFormattedComment(preparedRoute.applicantComment, comment)
  );
};

const updatePreparedRouteToCancel = async (
  state: RoutesState,
  updateApi: ReturnType<
    typeof useUpdatePreparedRoutesPreparedRouteIdPreparedRouteApprovalsApi
  >
) => {
  const { selectedPreparedRoute: preparedRoute, comment } = state;

  if (!preparedRoute) throw new Error("preparedRoute is not selected.");

  return updateApi(
    preparedRoute.id,
    "unapplied",
    getFormattedComment(preparedRoute.applicantComment, comment)
  );
};

const deletePreparedRoute = async (
  state: RoutesState,
  delApi: ReturnType<typeof useDelPreparedRouteApi>
) => {
  const { selectedPreparedRoute: preparedRoute } = state;

  if (!preparedRoute) throw new Error("preparedRoute is not selected.");

  return delApi(preparedRoute.id);
};

export const getPreparedRouteApprovalByAPIResponse = (
  preparedRouteApproval: any
): PreparedRouteApproval => ({
  id: preparedRouteApproval.id,
  preparedRouteId: preparedRouteApproval.prepared_route_id,
  applicantComment: preparedRouteApproval.applicant_comment || "",
  approvalStatus: preparedRouteApproval.approval_status,
  approvalStatusName: preparedRouteApproval.approval_status_name,
  approverComment: preparedRouteApproval.approver_comment,
});

function RoutesContent(): ReactElement | null {
  const auth = useContext(AuthContext);
  const [state, dispatch] = useReducer(reducer, initialState);

  const getUserAddressApi = useGetUserAddressApi();
  const getPreparedRoutes = useGetPreparedRoutesApi();
  const postPreparedRoutesApi = usePostPreparedRoutesApi();
  const delPreparedRouteApi = useDelPreparedRouteApi();
  const updatePreparedRoutePriorityApi = useUpdatePreparedRoutePriorityApi();
  const updatePreparedRouteExpirationDateApi =
    useUpdatePreparedRouteExpirationDateApi();
  const updatePreparedRoutesPreparedRouteIdPreparedRouteApprovalsApi =
    useUpdatePreparedRoutesPreparedRouteIdPreparedRouteApprovalsApi();

  useEffect(() => {
    (async () => {
      try {
        const data = await getPreparedRoutes();
        const preparedRoutes = data.reduce(
          (acc: PreparedRoute[], preparedRoute: any) => [
            ...acc,
            getPreparedRouteByAPIResponse(preparedRoute),
          ],
          []
        );

        dispatch({
          type: "SET_PREPARED_ROUTES",
          payload: {
            preparedRoutes,
          },
        });
        const homeAddress = await getUserAddressApi();
        dispatch({
          type: "SET_HOME_ADDRESS",
          payload: { homeAddress },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      } finally {
        dispatch({
          type: "SET_IS_PREPARED_ROUTES_LOADED",
          payload: {
            isPreparedRoutesLoaded: true,
          },
        });
      }
    })();
  }, [auth, getPreparedRoutes, getUserAddressApi]);

  const updatePreparedRouteApproval = async (action: "apply" | "cancel") => {
    dispatch({
      type: "SET_IS_UPDATE_PREPARED_ROUTE_IN_PROGRESS",
      payload: { isUpdatePreparedRouteInProgress: true },
    });

    try {
      // eslint-disable-next-line no-underscore-dangle
      let _approval;
      switch (action) {
        case "apply":
          _approval = await updatePreparedRouteToApply(
            state,
            updatePreparedRoutesPreparedRouteIdPreparedRouteApprovalsApi
          );
          break;
        case "cancel":
          _approval = await updatePreparedRouteToCancel(
            state,
            updatePreparedRoutesPreparedRouteIdPreparedRouteApprovalsApi
          );
          break;
        default:
          throw new Error(`invalid action: ${action}`);
      }
      const approval = getPreparedRouteApprovalByAPIResponse(_approval);
      const selectedPreparedRoute = {
        ...state.selectedPreparedRoute,
      };
      selectedPreparedRoute.approvalStatus = approval.approvalStatus;
      selectedPreparedRoute.approvalStatusName = approval.approvalStatusName;
      selectedPreparedRoute.applicantComment = approval.applicantComment;

      dispatch({
        type: "SET_PREPARED_ROUTE",
        payload: { preparedRoute: selectedPreparedRoute as PreparedRoute },
      });

      switch (action) {
        case "apply":
          dispatch({
            type: "SET_IS_UPDATE_PREPARED_ROUTE_COMPLETED",
            payload: {
              isUpdatePreparedRouteCompleted: true,
              selectedPreparedRoute: selectedPreparedRoute as PreparedRoute,
            },
          });
          break;
        default:
          dispatch({
            type: "TOGGLE_ROUTE_APPLICATION_DIALOG",
          });
          break;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      dispatch({
        type: "SET_IS_UPDATE_PREPARED_ROUTE_IN_PROGRESS",
        payload: { isUpdatePreparedRouteInProgress: false },
      });
    }
  };

  return (
    <>
      <header>
        <Navbar />
      </header>
      <main role="main" className="container">
        <Section>
          <SystemNotification />
          {state.isHomeAddressNotLoaded && (
            <div className="notification is-danger is-light">
              自宅住所が登録されていないため通勤経路の登録が出来ません。
            </div>
          )}
          <div className="cw-routes-header">
            <h2 className="title is-2 cw-routes-header-title">通勤経路</h2>
            <div className="cw-routes-header-actions">
              <div className="buttons has-addons">
                <Button
                  color="primary"
                  onClick={() => {
                    dispatch({
                      type: "TOGGLE_NEW_WORKPLACE_DIALOG",
                    });
                  }}
                  disabled={state.isHomeAddressNotLoaded}
                >
                  新規通勤地
                </Button>
              </div>
            </div>
          </div>
          <RoutesTable
            preparedRoutesByWorkplaces={state.preparedRoutesByWorkplaces}
            isPreparedRoutesLoaded={state.isPreparedRoutesLoaded}
            isUpdatePreparedRoutesInProgress={
              state.isUpdatePreparedRouteInProgress
            }
            toggleNewRouteDialog={(
              arrivalId?: number,
              arrivalName?: string,
              departureId?: number,
              departureName?: string
            ) => {
              dispatch({
                type: "TOGGLE_NEW_ROUTE_DIALOG",
                payload: {
                  arrivalId,
                  arrivalName,
                  departureId,
                  departureName,
                },
              });
            }}
            changePreparedRoute={(
              arrivalId: number,
              arrivalName: string,
              departureId: number,
              departureName: string,
              preparedRouteToChange: PreparedRoute
            ) => {
              dispatch({
                type: "TOGGLE_NEW_ROUTE_DIALOG_TO_CHANGE",
                payload: {
                  arrivalId,
                  arrivalName,
                  departureId,
                  departureName,
                  preparedRouteToChange,
                },
              });
            }}
            toggleRouteApplicationDialogToApply={(selectedPreparedRoute) => {
              dispatch({
                type: "TOGGLE_ROUTE_APPLICATION_DIALOG_TO_APPLY",
                payload: { selectedPreparedRoute },
              });
            }}
            toggleRouteApplicationDialogToCancel={(selectedPreparedRoute) => {
              dispatch({
                type: "TOGGLE_ROUTE_APPLICATION_DIALOG_TO_CANCEL",
                payload: { selectedPreparedRoute },
              });
            }}
            updatePreparedRoutePriority={useCallback(
              async (preparedRoute: PreparedRoute) => {
                dispatch({
                  type: "SET_IS_UPDATE_PREPARED_ROUTE_IN_PROGRESS",
                  payload: { isUpdatePreparedRouteInProgress: true },
                });

                try {
                  await updatePreparedRoutePriority(
                    preparedRoute,
                    updatePreparedRoutePriorityApi
                  );

                  // eslint-disable-next-line no-restricted-syntax
                  for (const pr of state.preparedRoutes) {
                    if (
                      pr.departureId === preparedRoute.departureId &&
                      pr.arrivalId === preparedRoute.arrivalId
                    ) {
                      pr.priority = pr.id === preparedRoute.id;
                    }
                  }
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.log(error);
                } finally {
                  dispatch({
                    type: "SET_IS_UPDATE_PREPARED_ROUTE_IN_PROGRESS",
                    payload: { isUpdatePreparedRouteInProgress: false },
                  });
                }
              },
              [state, updatePreparedRoutePriorityApi]
            )}
            toggleExpirationDateDialog={(preparedRoute) => {
              dispatch({
                type: "TOGGLE_EXPIRATION_DATE_DIALOG",
                payload: { preparedRoute },
              });
            }}
          />
        </Section>

        <RouteApplicationDialog
          isVisible={state.isRouteApplicationDialogVisible}
          isUpdateCompleted={state.isUpdatePreparedRouteCompleted}
          isUpdateInProgress={state.isUpdatePreparedRouteInProgress}
          preparedRoute={state.selectedPreparedRoute}
          comment={state.comment}
          mode={state.routeApplicationDialogMode}
          toggleDialog={() => {
            dispatch({
              type: "TOGGLE_ROUTE_APPLICATION_DIALOG",
            });
          }}
          changeComment={(comment) => {
            dispatch({
              type: "CHANGE_COMMENT",
              payload: { comment },
            });
          }}
          updateToApply={() => updatePreparedRouteApproval("apply")}
          updateToCancel={() => updatePreparedRouteApproval("cancel")}
        />
        <NewWorkplaceDialog
          isVisible={state.isNewWorkplaceDialogVisible}
          toggleDialog={() => {
            dispatch({
              type: "TOGGLE_NEW_WORKPLACE_DIALOG",
            });
          }}
          registerWorkplace={(address) => {
            dispatch({
              type: "REGISTER_WORKPLACE",
              payload: {
                selectedAddress: address,
                home: state.homeAddress,
              },
            });
          }}
        />
        <NewRouteDialog
          isVisible={state.isNewRouteDialogVisible}
          isCreateInProgress={state.isCreatePreparedRouteInProgress}
          arrivalId={state.arrivalId}
          arrivalName={state.arrivalName}
          createPreparedRoute={useCallback(
            async (suggestedPreparedRoute: SuggestedPreparedRoute) => {
              dispatch({
                type: "SET_IS_CREATE_PREPARED_ROUTE_IN_PROGRESS",
                payload: { isCreatePreparedRouteInProgress: true },
              });

              try {
                const preparedRoute = await createPreparedRoute(
                  suggestedPreparedRoute,
                  postPreparedRoutesApi
                );

                let updatedPreparedRoutes: PreparedRoute[];
                if (state.selectedPreparedRoute) {
                  await deletePreparedRoute(state, delPreparedRouteApi);
                  updatedPreparedRoutes = [
                    ...state.preparedRoutes.filter(
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      (pr) => pr.id !== state.selectedPreparedRoute!.id
                    ),
                    getPreparedRouteByAPIResponse(preparedRoute),
                  ];
                } else {
                  updatedPreparedRoutes = [
                    ...state.preparedRoutes,
                    getPreparedRouteByAPIResponse(preparedRoute),
                  ];
                }

                dispatch({
                  type: "SET_PREPARED_ROUTES",
                  payload: {
                    preparedRoutes: updatedPreparedRoutes,
                  },
                });

                dispatch({
                  type: "TOGGLE_NEW_ROUTE_DIALOG",
                  payload: {},
                });
              } catch (error) {
                // eslint-disable-next-line no-console
                console.log(error);
              } finally {
                dispatch({
                  type: "SET_IS_CREATE_PREPARED_ROUTE_IN_PROGRESS",
                  payload: { isCreatePreparedRouteInProgress: false },
                });
              }
            },
            [state, postPreparedRoutesApi, delPreparedRouteApi]
          )}
          toggleDialog={() => {
            dispatch({
              type: "TOGGLE_NEW_ROUTE_DIALOG",
              payload: {},
            });
          }}
        />
        <ExpirationDateDialog
          isVisible={state.isExpirationDateDialogVisible}
          preparedRoute={state.selectedPreparedRoute}
          registerExpirationDate={useCallback(
            async (preparedRoute: PreparedRoute) => {
              dispatch({
                type: "SET_IS_EXPIRATION_DATE_IN_PROGRESS",
                payload: { isExpirationDateInProgress: true },
              });
              try {
                await updatePreparedRouteExpirationDate(
                  preparedRoute,
                  updatePreparedRouteExpirationDateApi
                );
              } finally {
                dispatch({
                  type: "SET_IS_EXPIRATION_DATE_IN_PROGRESS",
                  payload: { isExpirationDateInProgress: false },
                });
              }
            },
            [updatePreparedRouteExpirationDateApi]
          )}
          toggleDialog={() => {
            dispatch({
              type: "TOGGLE_EXPIRATION_DATE_DIALOG",
              payload: {},
            });
          }}
        />
      </main>
    </>
  );
}

export function Routes(): ReactElement | null {
  return (
    <GlobalErrorScreen>
      <RoutesContent />
    </GlobalErrorScreen>
  );
}
/* eslint-enable */
